import { useContext, useEffect } from 'react';
import { Layer } from '@deck.gl/core';
import { DeckOverlayContext } from './DeckOverlay';
import { DeckLayerGroup } from './DeckOverlay.constants';

// Stable identity so unregistering never churns the overlay's layer array.
const NO_LAYERS: Layer[] = [];

/**
 * Contributes `layers` to the map's shared deck.gl overlay under `group`,
 * replacing whatever that group previously registered and clearing it on
 * unmount.
 *
 * `layers` is used by identity, so callers must memoize it — passing a fresh
 * array every render would rebuild every deck layer on the map each time.
 */
export const useDeckLayers = (group: DeckLayerGroup, layers: Layer[]): void => {
  const context = useContext(DeckOverlayContext);

  if (!context) {
    throw new Error('useDeckLayers must be used within a <DeckOverlay>');
  }

  const { registerLayers } = context;

  useEffect(() => {
    registerLayers(group, layers);
  }, [registerLayers, group, layers]);

  useEffect(
    () => () => {
      registerLayers(group, NO_LAYERS);
    },
    [registerLayers, group],
  );
};
